import React, {
	type ReactNode,
	type Ref,
	useCallback,
	useId,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';

type ThreadRail = 'comment' | 'standalone';

export type RenderPrivateReply = (card: ReactNode, threadRail?: ThreadRail) => ReactNode;

// Compiled accepts the surface-coloured border the card elbows use, which @atlaskit/css does not.
// Continue from the straight part of the card's elbow down to the next card. Starting
// before the elbow's rounded end avoids a visible break in the vertical rail.
const styles = cssMap({
	slot: {
		position: 'relative',
	},
	continuedRail: {
		'&::after': {
			content: "''",
			position: 'absolute',
			top: token('space.200'),
			bottom: 0,
			borderInlineStart: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
			pointerEvents: 'none',
		},
	},
	comment: {
		'&::after': {
			insetInlineStart: token('space.150'),
		},
	},
	standalone: {
		'&::after': {
			insetInlineStart: token('space.300'),
		},
	},
});

const PrivateReplyTarget = ({ targetRef }: { targetRef: Ref<HTMLDivElement> }) => (
	<div ref={targetRef} data-testid="comment-agent-status-end" />
);

// Cards are portalled in mount order, so the last mounted slot is the last card on screen.
const PrivateReplySlot = ({
	children,
	threadRail,
	lastSlotId,
	registerSlot,
}: {
	children: ReactNode;
	threadRail?: ThreadRail;
	lastSlotId: string | undefined;
	registerSlot: (id: string) => () => void;
}) => {
	const id = useId();
	useLayoutEffect(() => registerSlot(id), [id, registerSlot]);
	const continuesRail = !!threadRail && lastSlotId !== id;
	return (
		<div
			css={[
				styles.slot,
				continuesRail && styles.continuedRail,
				continuesRail && threadRail && styles[threadRail],
			]}
		>
			{children}
		</div>
	);
};

/**
 * Moves a thread's private agent replies to a target rendered after its replies.
 * When several cards wait in one thread, each card but the last carries the rail
 * down to the next card.
 */
export const useCommentAgentPrivateReply = (
	enabled: boolean,
): { renderPrivateReply: RenderPrivateReply | undefined; privateReplyTarget: ReactNode } => {
	const [target, setTarget] = useState<HTMLDivElement | null>(null);
	const [slotIds, setSlotIds] = useState<readonly string[]>([]);
	const registerSlot = useCallback((id: string) => {
		setSlotIds((ids) => [...ids, id]);
		return () => setSlotIds((ids) => ids.filter((slotId) => slotId !== id));
	}, []);
	const lastSlotId = slotIds[slotIds.length - 1];
	const renderPrivateReply = useMemo<RenderPrivateReply | undefined>(
		() =>
			enabled &&
			target &&
			expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
				? (card, threadRail) =>
						createPortal(
							<PrivateReplySlot
								threadRail={threadRail}
								lastSlotId={lastSlotId}
								registerSlot={registerSlot}
							>
								{card}
							</PrivateReplySlot>,
							target,
						)
				: undefined,
		[enabled, target, lastSlotId, registerSlot],
	);
	return {
		renderPrivateReply,
		privateReplyTarget:
			enabled && expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ? (
				<PrivateReplyTarget targetRef={setTarget} />
			) : null,
	};
};
